/**
 * Dialog animation utilities for slide-up/slide-down animations
 * Usage: Apply to dialog elements for smooth bottom-aligned animations
 */

export interface DialogAnimationOptions {
  duration?: number; // Animation duration in milliseconds
  easing?: string; // CSS easing function
}

const DEFAULT_OPTIONS: DialogAnimationOptions = {
  duration: 300,
  easing: 'ease-out'
};

/**
 * Slides a dialog element up from the bottom of the screen
 * @param dialog - The dialog element to animate
 * @param options - Animation options (duration, easing)
 * @returns Promise that resolves when animation completes
 */
export function slideUpDialog(
  dialog: HTMLDialogElement,
  options: DialogAnimationOptions = {}
): Promise<void> {
  const { duration } = { ...DEFAULT_OPTIONS, ...options };

  return new Promise((resolve) => {
    // Ensure we start from the bottom (off-screen)
    dialog.classList.remove('slide-up');
    dialog.classList.remove('slide-down');

    // Force a reflow to ensure the removal takes effect
    void dialog.offsetHeight;

    // Add slide-up class to trigger animation
    dialog.classList.add('slide-up');

    // Resolve after animation completes
    setTimeout(() => {
      resolve();
    }, duration);
  });
}

/**
 * Slides a dialog element down and closes it
 * @param dialog - The dialog element to animate
 * @param options - Animation options (duration, easing)
 * @returns Promise that resolves when animation completes
 */
export function slideDownDialog(
  dialog: HTMLDialogElement,
  options: DialogAnimationOptions = {}
): Promise<void> {
  const { duration, easing } = { ...DEFAULT_OPTIONS, ...options };

  return new Promise((resolve) => {
    // Add slide-down class to trigger closing animation
    dialog.classList.remove('slide-up');
    dialog.classList.add('slide-down');

    // Close dialog after animation completes
    setTimeout(() => {
      dialog.close();
      dialog.classList.remove('slide-down');
      resolve();
    }, duration);
  });
}

/**
 * Opens a dialog with slide-up animation
 * @param dialog - The dialog element to open
 * @param options - Animation options
 */
export async function openDialogWithSlideUp(
  dialog: HTMLDialogElement,
  options: DialogAnimationOptions = {}
): Promise<void> {
  if (!dialog) return;

  // Ensure dialog starts off-screen before showing
  dialog.classList.remove('slide-up');
  dialog.classList.remove('slide-down');

  // Open the dialog first (required for showModal)
  dialog.showModal();

  // Small delay to ensure DOM is ready
  await new Promise(resolve => setTimeout(resolve, 10));

  // Trigger slide-up animation
  await slideUpDialog(dialog, options);
}

/**
 * Closes a dialog with slide-down animation
 * @param dialog - The dialog element to close
 * @param options - Animation options
 */
export async function closeDialogWithSlideDown(
  dialog: HTMLDialogElement,
  options: DialogAnimationOptions = {}
): Promise<void> {
  if (!dialog) return;

  await slideDownDialog(dialog, options);
}
